import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { AutoComplete, AutoCompleteModule } from 'primeng/autocomplete';
import { SelectButtonModule } from 'primeng/selectbutton';
import { DataService, Note, Tag, TagMutationPayload } from '../../../_service/data.service';
import { Space } from '../../../_service/space.service';
import { AlertService } from '../../../_service/alert.service';
import {
  DEFAULT_TAG_TYPE,
  TAG_SCOPE,
  TagScope,
  TagSuggestionGroup,
  filterPrivateTags,
  filterSpaceTags,
  groupTagsByScope,
  hasCurrentTagScope,
  isPrivateTag,
  resolveNoteSpaceTags,
} from '../../../_util/tag-scope.util';

/**
 * A note's tags with × buttons plus the "Assign tag…" field, as in Edit — for changing one note's tags
 * from Home (List view's Tags cell, or the phone's "Edit tags" dialog) without opening the note.
 *
 * It only says what the user did (tagAdded / tagRemoved, and tagCreated for a tag it just created);
 * HomeComponent applies the change: Space tags as a tag-only save of Note.Tags (Modified date
 * unchanged), private tags through the private-tag-assignment API (the Note isn't touched at all).
 */
@Component({
  selector: 'app-note-tag-editor',
  standalone: true,
  imports: [FormsModule, AutoCompleteModule, SelectButtonModule],
  templateUrl: './note-tag-editor.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NoteTagEditorComponent implements OnChanges, AfterViewInit {
  private readonly dataService = inject(DataService);
  private readonly alertService = inject(AlertService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  @Input({ required: true }) note!: Note;
  /** Every tag Home knows: the user's private tags and the Space tags of their Spaces. */
  @Input() catalog: Tag[] = [];
  /** The user's private tags on this note. */
  @Input() privateTagIds: readonly string[] = [];
  @Input() spaces: Space[] = [];
  @Input() isOnline = true;
  /** Focus "Assign tag…" when shown (the desktop cell — not the phone dialog, where it would pop up the keyboard). */
  @Input() autoFocus = false;

  @Output() tagAdded = new EventEmitter<Tag>();
  @Output() tagRemoved = new EventEmitter<Tag>();
  @Output() tagCreated = new EventEmitter<Tag>();

  @ViewChild('quickTagAutocomplete') quickTagAutocomplete?: AutoComplete;

  readonly TAG_SCOPE = TAG_SCOPE;
  readonly tagScopeOptions = [
    { label: 'Space tag', value: TAG_SCOPE.SPACE, icon: 'pi pi-users' },
    { label: 'Private tag', value: TAG_SCOPE.PRIVATE, icon: 'pi pi-lock' },
  ];
  newTagScope: TagScope = TAG_SCOPE.SPACE;
  quickTagName: string | Tag = '';
  tagSuggestionGroups: TagSuggestionGroup[] = [];
  isTagMutating = false;

  /** The note's Space tags (Note.Tags) and the user's private tags on it. */
  noteTags: Tag[] = [];
  privateNoteTags: Tag[] = [];
  private spaceTagCatalog: Tag[] = [];
  private privateTagCatalog: Tag[] = [];

  ngOnChanges(): void {
    const spaceId = this.noteSpaceId;
    this.spaceTagCatalog = sortTags(filterSpaceTags(this.catalog, spaceId));
    this.privateTagCatalog = sortTags(filterPrivateTags(this.catalog));
    this.noteTags = resolveNoteSpaceTags(this.note?.Tags, this.spaceTagCatalog, {
      privateTagIds: [...this.privateTagCatalog.map((t) => t._id), ...this.privateTagIds],
      spaceId,
    });
    const privateById = new Map(this.privateTagCatalog.map((t) => [t._id, t] as const));
    this.privateNoteTags = sortTags(this.privateTagIds.map((id) => privateById.get(id)).filter((t): t is Tag => !!t));
    this.refreshTagSuggestions(this.quickTagInputText);
  }

  ngAfterViewInit(): void {
    if (this.autoFocus) {
      // Focusing also scrolls the field into view (e.g. a Tags column scrolled off the side of a wide table).
      setTimeout(() => {
        const input: HTMLInputElement | undefined = this.quickTagAutocomplete?.inputEL?.nativeElement;
        input?.focus({ preventScroll: true });
        const box = this.host.nativeElement.closest('td') ?? this.host.nativeElement;
        box.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      });
    }
  }

  // ───── Space / permissions (as in Edit) ─────

  private get privateSpaceId(): string | undefined {
    return this.spaces.find((space) => space.type === 'PRIVATE')?.id;
  }

  /** The note's Space (a note from before Spaces is in the user's PRIVATE Space). */
  private get noteSpaceId(): string | null {
    return this.note?.spaceId || this.privateSpaceId || null;
  }

  private get noteSpace(): Space | undefined {
    const id = this.noteSpaceId;
    return id ? this.spaces.find((space) => space.id === id) : undefined;
  }

  private get canWriteSpace(): boolean {
    const space = this.noteSpace;
    if (!space || space.type !== 'SHARED') return true;
    return space.current_user_role === 'OWNER' || space.current_user_role === 'ADMIN' || space.current_user_role === 'EDITOR';
  }

  /** Space tags can be created only in a SHARED Space the user can write to (else new tags are private). */
  get canCreateSpaceTags(): boolean {
    return this.noteSpace?.type === 'SHARED' && this.canWriteSpace;
  }

  get effectiveNewTagScope(): TagScope {
    return this.canCreateSpaceTags ? this.newTagScope : TAG_SCOPE.PRIVATE;
  }

  /** Private tag assignments live on the server only, for a note the server already has. */
  get canChangePrivateTags(): boolean {
    return this.isOnline && !!this.note && !this.note.localDraft && this.note.syncStatus !== 'new';
  }

  get privateTagsBlockedReason(): string {
    if (!this.isOnline) return 'Private tags can only be changed while online.';
    return 'Save the note first — private tags can be added once it is saved.';
  }

  get quickTagInputText(): string {
    return typeof this.quickTagName === 'string' ? this.quickTagName : this.quickTagName?.name ?? '';
  }

  get quickTagExistsInScope(): boolean {
    const name = this.quickTagInputText.trim();
    return !!name && !!this.findTagByName(name, this.effectiveNewTagScope);
  }

  // ───── Add / remove ─────

  removeSpaceTag(tag: Tag): void {
    if (!this.canWriteSpace) {
      this.alertService.warn('You can only view this Space — its tags can’t be changed.');
      return;
    }
    this.tagRemoved.emit(tag);
  }

  removePrivateTag(tag: Tag): void {
    if (!this.canChangePrivateTags) {
      this.alertService.warn(this.privateTagsBlockedReason);
      return;
    }
    this.tagRemoved.emit(tag);
  }

  searchTagSuggestions(event: { query?: string }): void {
    this.refreshTagSuggestions(event?.query);
  }

  showAvailableTagSuggestions(): void {
    this.refreshTagSuggestions();
  }

  onTagAutocompleteSelected(event: { value?: Tag }): void {
    const tag = event?.value;
    if (!tag) return;
    this.resetQuickTagInput();
    this.attachTag(tag);
  }

  async addQuickTag(): Promise<void> {
    if (typeof this.quickTagName !== 'string') {
      const tag = this.quickTagName;
      this.resetQuickTagInput();
      this.attachTag(tag);
      return;
    }
    const value = this.quickTagInputText.trim();
    if (!value) return;

    // Names are matched within the scope being added to only (a Space tag and a private tag may share a name).
    const scope = this.effectiveNewTagScope;
    const existing = this.findTagByName(value, scope);
    if (existing) {
      this.resetQuickTagInput();
      if (this.isTagOnNote(existing)) {
        this.alertService.warn(`Tag "${value}" is already on this note.`);
      } else {
        this.attachTag(existing);
      }
      return;
    }
    if (scope === TAG_SCOPE.PRIVATE && !this.canChangePrivateTags) {
      this.alertService.warn(this.privateTagsBlockedReason);
      return;
    }

    const payload: TagMutationPayload = {
      name: value,
      tagType: DEFAULT_TAG_TYPE,
      slug: slugify(value),
      color: '#2563EB',
      icon: 'tag',
      parentTagId: null,
      sortOrder: this.noteTags.reduce((max, tag) => Math.max(max, tag.sortOrder ?? 0), 0) + 10,
      isActive: true,
    };
    if (scope === TAG_SCOPE.SPACE) {
      payload.spaceId = this.noteSpaceId ?? undefined;
    }

    this.isTagMutating = true;
    try {
      const resp = await firstValueFrom(this.dataService.createTag(payload));
      if (resp.status !== 'success' || !resp.data) {
        this.alertService.error(resp.error?.message || 'Failed to create tag.');
        return;
      }
      this.resetQuickTagInput();
      this.tagCreated.emit(resp.data);
      this.attachTag(resp.data);
      this.alertService.success(`Tag "${resp.data.name}" created.`);
    } catch (err: any) {
      this.alertService.error(err?.message || 'Failed to create tag.');
    } finally {
      this.isTagMutating = false;
      this.cdr.markForCheck();
    }
  }

  private attachTag(tag: Tag): void {
    if (!hasCurrentTagScope(tag) || this.isTagOnNote(tag)) return;
    if (isPrivateTag(tag)) {
      if (!this.canChangePrivateTags) {
        this.alertService.warn(this.privateTagsBlockedReason);
        return;
      }
    } else if (!this.canWriteSpace) {
      this.alertService.warn('You can only view this Space — its tags can’t be changed.');
      return;
    }
    this.tagAdded.emit(tag);
  }

  private isTagOnNote(tag: Tag): boolean {
    return this.noteTags.some((t) => t._id === tag._id) || this.privateTagIds.includes(tag._id);
  }

  private findTagByName(name: string, scope: TagScope): Tag | undefined {
    const catalog = scope === TAG_SCOPE.PRIVATE ? this.privateTagCatalog : this.spaceTagCatalog;
    const slug = slugify(name);
    const lower = name.toLowerCase();
    return catalog.find((tag) => tag.slug === slug || tag.name.toLowerCase() === lower);
  }

  /** Active tags not yet on the note: this note's Space tags (if the user may change them), then private. */
  private refreshTagSuggestions(query: unknown = ''): void {
    const q = String(query ?? '').trim().toLowerCase();
    const onNote = new Set([...this.noteTags.map((t) => t._id), ...this.privateTagIds]);
    const matches = (tag: Tag) =>
      tag.isActive !== false &&
      !onNote.has(tag._id) &&
      (!q || [tag.name, tag.slug, tag.tagType, tag.icon ?? ''].some((v) => String(v ?? '').toLowerCase().includes(q)));
    const spaceTags = this.canWriteSpace ? this.spaceTagCatalog.filter(matches) : [];
    this.tagSuggestionGroups = groupTagsByScope(spaceTags, this.privateTagCatalog.filter(matches));
  }

  private resetQuickTagInput(): void {
    this.quickTagName = '';
    this.refreshTagSuggestions();
    // The autocomplete writes the picked tag into its field after onSelect: clear it once that's done.
    setTimeout(() => {
      this.quickTagName = '';
      const input: HTMLInputElement | undefined = this.quickTagAutocomplete?.inputEL?.nativeElement;
      if (input) input.value = '';
      this.cdr.markForCheck();
    });
  }
}

function sortTags(tags: Tag[]): Tag[] {
  return [...tags].sort((a, b) => {
    const orderA = a.sortOrder ?? Number.MAX_SAFE_INTEGER;
    const orderB = b.sortOrder ?? Number.MAX_SAFE_INTEGER;
    return orderA !== orderB ? orderA - orderB : a.name.localeCompare(b.name);
  });
}

function slugify(value: string): string {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}
